import { rrr } from '../js00'
import { tWechatUserid, tWechatUserinfo } from '@/axios/api'
import accessDetector from '@/utils/accessDetector'

// 配置
const config = {
  corpId: 'ww43bd578453b5d297',  // 从企业微信后台获取
  agentId: '1000013', // 应用管理中的AgentId
  // 动态设置回调地址，携带来源信息
  getRedirectUri() {
    // 获取当前访问来源
    const source = accessDetector.getSource()
    return encodeURIComponent(
      window.location.origin + '/wechatauthcall' + 
      `?source=${source}&timestamp=${Date.now()}`
    )
  }
};

class WechatAuth {
  /**
   * 根据当前页面路径动态获取 agentId
   */
  static getAgentId() {
    const path = window.location.pathname
    
    // 从当前路径判断
    if (path.includes('/pgarevalscan')) {
      return '1000013'  // GE Scan 应用
    } else if (path.includes('/wellincenter')) {
      return '1000010'  // Wellin Center 应用
    }
    
    // 从保存的重定向地址判断（登录后回调时使用）
    const redirectPath = localStorage.getItem('auth_redirect') || ''
    if (redirectPath.includes('/pgarevalscan')) return '1000013'
    if (redirectPath.includes('/wellincenter')) return '1000010'
    
    console.warn('未识别的应用路径，使用默认agentId: 1000010')
    return '1000010'
  }

  /**
   * 静默授权 - 获取用户UserId
   * @param {string} state - 可选，用于防止CSRF攻击
   */
  static silentAuth(state = '') {
    // 使用动态的回调地址
    const agentId = this.getAgentId()
    const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?
      appid=${config.corpId}&
      redirect_uri=${config.getRedirectUri()}&
      response_type=code&
      scope=snsapi_base&
      state=${state || 'silent'}&
      agentid=${agentId}#wechat_redirect`;
    
    // 去除换行和空格
    window.location.href = authUrl.replace(/\s+/g, '');
  }

  /**
   * 手动授权 - 获取用户详细信息
   * @param {string} state - 可选，用于防止CSRF攻击
   */
  static handAuth(state = '') {
    // 使用动态的回调地址，并添加来源标记
    const agentId = this.getAgentId();
    const finalState = state || `manual_${agentId}_${Date.now()}`;  // 确保格式统一
    const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?
      appid=${config.corpId}&
      redirect_uri=${config.getRedirectUri()}&
      response_type=code&
      scope=snsapi_userinfo&
      state=${finalState}&
      agentid=${agentId}#wechat_redirect`;
    
    window.location.href = authUrl.replace(/\s+/g, '');
  }

  /**
   * 智能授权 - 根据访问来源自动选择授权方式
   * 这个方法可以替代原来的handAuth
   */
  static smartAuth(state = '') {
    // 根据访问来源决定授权方式
    if (accessDetector.isWxWork()) {
      // 企业微信访问，使用企业微信授权
      this.handAuth(state)
    } else if (accessDetector.isWeChat()) {
      // 普通微信访问，可以添加微信公众平台授权逻辑
      console.warn('普通微信访问，请配置微信公众平台授权')
      // 这里可以调用微信公众平台的授权
      // this.wechatPublicAuth(state)
    } else {
      // 浏览器访问，提示不支持
      alert('请在微信或企业微信中打开此页面进行授权登录')
      return false
    }
  }

  /**
   * 处理回调 - 获取code并交换用户信息
   */
  static async handleCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const source = urlParams.get('source'); // 获取来源参数
    
    if (!code) {
      throw new Error('未获取到授权码');
    }

    try {
      // 根据来源参数更新访问来源
      if (source && ['wxwork', 'wechat'].includes(source)) {
        accessDetector.clearSource() // 清除旧的
        localStorage.setItem('accessSource', source)
      }

      // 从 state 中解析 agentId（如果存在）
      let agentId = null
      if (state) {
        const parts = state.split('_')
        // 格式: manual_1000013_1234567890 或 silent_1000010_1234567890
        if (parts.length >= 2 && (parts[0] === 'manual' || parts[0] === 'silent')) {
          agentId = parts[1]
        }
      }
      
      // 如果 state 中没有 agentId，则从路径判断
      if (!agentId) {
        agentId = this.getAgentId()
      }
      
      // 根据state判断授权类型
      if (state && state.includes('silent')) {
        return await this.tUserId(code, agentId)  // 传入 agentId
      } else {
        return await this.tUserInfo(code, agentId)  // 传入 agentId
      }
    } catch (error) {
      console.error('授权处理失败:', error);
      throw error;
    }
  }

  /**
   * 通过code获取UserId
   */
  static async tUserId(code, agentId) {
    // 这里需要调用你的后端接口
    const eucode = 'w_' + String(rrr(1, 999999999))
    const eagentId = agentId || this.getAgentId()
    const response = await tWechatUserid({
      code: code,
      agentId: eagentId,
      ucode: eucode
    });
    if (response.data && response.code === 0) {
      return {ucode: eucode, data: response.data, token: response.token};
    }
  }

  /**
   * 通过code获取用户详细信息
   */
  static async tUserInfo(code, agentId) {
    // 这里需要调用你的后端接口
    const eucode = 'w_' + String(rrr(1, 999999999))
    const eagentId = agentId || this.getAgentId()
    const response = await tWechatUserinfo({
      code: code,
      agentId: eagentId,
      ucode: eucode
    });
    
    if (response.data && response.code === 0) {
      return {ucode: eucode, data: response.data, token: response.token};
    }
  }

  // 检查是否在微信/企业微信环境中
  static isInWechatEnvironment() {
    return accessDetector.isWxWork() || accessDetector.isWeChat()
  }

  // 当前环境描述
  static getEnvironmentInfo() {
    const source = accessDetector.getSource()
    const info = {
      'wxwork': '企业微信',
      'wechat': '微信',
      'browser': '浏览器'
    }
    return {
      source: source,
      name: info[source] || '未知',
      isWechat: source === 'wxwork' || source === 'wechat',
      isWxWork: source === 'wxwork'
    }
  }
}

export default WechatAuth;